import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import {
  Brain, LayoutDashboard, FolderInput, Layers3, GitBranch, FileJson,
  MessageSquare, BellDot, LogOut, Sun, Moon, TrendingDown, KeyRound, AlertTriangle,
  Users, Settings, GitPullRequest, Notebook, ShieldOff, Plug, GraduationCap, Boxes, ScrollText, Mail, Bell, Building2,
  Activity, Bot, Timer, LibraryBig, CreditCard, Sparkles, Video,
} from "lucide-react";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import ConsentGate, { useConsentStatus } from "@/components/ConsentGate";
import ShortcutHint from "@/components/ShortcutHint";

const NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Overview", tid: "nav-overview" },
  { to: "/app/onboarding", icon: Sparkles, label: "Setup guide", tid: "nav-onboarding", adminOnly: true, onboardingBadge: true },
  { to: "/app/sources", icon: FolderInput, label: "Sources", tid: "nav-sources" },
  { to: "/app/knowledge", icon: Layers3, label: "Knowledge", tid: "nav-knowledge" },
  { to: "/app/map", icon: GitBranch, label: "Map", tid: "nav-map" },
  { to: "/app/skills", icon: FileJson, label: "Skills File", tid: "nav-skills" },
  { to: "/app/chat", icon: MessageSquare, label: "Ask AI", tid: "nav-chat" },
  { to: "/app/memory", icon: Notebook, label: "Individual memory", tid: "nav-memory" },
  { to: "/app/people", icon: Users, label: "People", tid: "nav-people" },
  { to: "/app/bot-access", icon: Bot, label: "Bot access", tid: "nav-bot-access" },
  { to: "/app/meetings", icon: Video, label: "Meetings", tid: "nav-meetings" },
  { to: "/app/knowledge-decay", icon: Timer, label: "Knowledge decay", tid: "nav-decay", adminOnly: true },
  { to: "/app/work-log", icon: Activity, label: "My work log", tid: "nav-worklog" },
  { to: "/app/work-intelligence", icon: Activity, label: "Work Intelligence", tid: "nav-wi", adminOnly: true },
  { to: "/app/induction", icon: GraduationCap, label: "Induction", tid: "nav-induction" },
  { to: "/app/entities", icon: Boxes, label: "Entities", tid: "nav-entities" },
  { to: "/app/audit", icon: ScrollText, label: "Audit log", tid: "nav-audit", adminOnly: true },
  { to: "/app/nudge-digest", icon: Bell, label: "Nudge digest", tid: "nav-nudge-digest", adminOnly: true },
  { to: "/app/enterprise-leads", icon: Building2, label: "Enterprise leads", tid: "nav-enterprise-leads", adminOnly: true },
  { to: "/app/template-library", icon: LibraryBig, label: "Template library", tid: "nav-template-library", adminOnly: true },
  { to: "/app/billing", icon: CreditCard, label: "Billing", tid: "nav-billing", adminOnly: true },
  { to: "/app/integrations", icon: Plug, label: "Integrations", tid: "nav-integrations" },
  { to: "/app/updates", icon: BellDot, label: "Live Updates", tid: "nav-updates" },
  { to: "/app/suggestions", icon: GitPullRequest, label: "Suggestions", tid: "nav-suggestions", badgeKey: "pending" },
  { to: "/app/gaps", icon: TrendingDown, label: "Gaps", tid: "nav-gaps" },
  { to: "/app/agent", icon: KeyRound, label: "Agent API", tid: "nav-agent", adminOnly: true },
  { to: "/app/members", icon: Users, label: "Members", tid: "nav-members", adminOnly: true },
  { to: "/app/settings", icon: Settings, label: "Settings", tid: "nav-settings", adminOnly: true },
];

export default function DashboardLayout() {
  const { user, workspace, role, isAdmin, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  const [suggStats, setSuggStats] = useState(null);
  const [showMagicBadge, setShowMagicBadge] = useState(() => {
    try { return document.cookie.includes("ragnes_magic_success=1"); }
    catch { return false; }
  });
  const [billing, setBilling] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [impersonation, setImpersonation] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  useEffect(() => {
    // Detect impersonation by reading the JWT payload (base64 middle segment).
    try {
      const tok = localStorage.getItem("cb_token");
      if (!tok) return;
      const payload = JSON.parse(atob(tok.split(".")[1]));
      if (payload.impersonated_by) {
        setImpersonation({ by: payload.impersonated_by, ws: payload.impersonated_workspace_id });
      }
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    if (!user) return;
    api.get("/workspace/onboarding-status").then((r) => setOnboarding(r.data)).catch(() => {});
  }, [user?.id]);
  useEffect(() => {
    // Auto-redirect fresh admins to /app/onboarding on their first dashboard hit.
    if (!isAdmin || !onboarding) return;
    if (onboarding.auto_show && window.location.pathname === "/app") {
      nav("/app/onboarding");
    }
  }, [onboarding?.auto_show, isAdmin]);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => {
    if (!showMagicBadge) return;
    // Clear the cookie so the badge doesn't reappear on refresh.
    document.cookie = "ragnes_magic_success=; Max-Age=0; path=/; secure; samesite=none";
    const t = setTimeout(() => setShowMagicBadge(false), 30000);
    return () => clearTimeout(t);
  }, [showMagicBadge]);
  // Crisp support widget — no-op when REACT_APP_CRISP_WEBSITE_ID is missing.
  useEffect(() => {
    const id = process.env.REACT_APP_CRISP_WEBSITE_ID;
    if (!id || typeof window === "undefined" || window.$crisp) return;
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = id;
    const s = document.createElement("script");
    s.src = "https://client.crisp.chat/l.js";
    s.async = true;
    document.head.appendChild(s);
    if (user?.email) window.$crisp.push(["set", "user:email", [user.email]]);
    if (user?.name) window.$crisp.push(["set", "user:nickname", [user.name]]);
  }, [user?.email, user?.name]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [h, s] = await Promise.all([
          api.get("/gaps/stats", { params: { lookback_days: 1 } }),
          api.get("/suggestions/stats").catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setHealth(h.data);
        setSuggStats(s.data);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [workspace?.id]);

  const visibleNav = NAV.filter((n) => {
    if (n.adminOnly && !isAdmin) return false;
    if (n.onboardingBadge && onboarding && (onboarding.all_complete || onboarding.skipped)) return false;
    return true;
  });
  const badges = { pending: suggStats?.pending || 0 };
  const { status: consent, refresh: refreshConsent } = useConsentStatus();

  return (
    <div className="flex h-screen w-full bg-background text-foreground">
      {impersonation && (
        <div data-testid="impersonation-banner"
             className="fixed top-0 inset-x-0 z-[9998] bg-red-600 text-white text-xs px-4 py-2 flex items-center justify-center gap-3">
          <span>⚠ You are viewing as {workspace?.name || "this workspace"} admin (impersonating as {impersonation.by}).</span>
          <button
            onClick={() => { localStorage.removeItem("cb_token"); sessionStorage.removeItem("cb_token_impersonate"); window.location.href = "/superadmin/workspaces"; }}
            className="underline hover:no-underline font-medium"
            data-testid="impersonation-exit">
            Exit impersonation →
          </button>
        </div>
      )}
      {showMagicBadge && (
        <div data-testid="magic-signin-badge"
             className="fixed top-4 left-1/2 -translate-x-1/2 z-[9998] px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/50 text-emerald-500 text-xs font-medium shadow-lg backdrop-blur-sm">
          ✓ Signed in via email link
        </div>
      )}
      {billing?.status === "trial" && billing.trial_days_left != null && !bannerDismissed && (
        <div data-testid="trial-banner"
             className="fixed top-0 inset-x-0 z-[9997] bg-sky-500/10 border-b border-sky-500/40 text-sky-500 text-xs px-4 py-2 flex items-center justify-center gap-3">
          <span>🎉 Free trial — {billing.trial_days_left} day{billing.trial_days_left === 1 ? "" : "s"} remaining</span>
          <a href="/app/billing" className="font-medium underline hover:no-underline">Upgrade now →</a>
          <button onClick={() => setBannerDismissed(true)}
                  className="ml-2 text-sky-500/70 hover:text-sky-500"
                  data-testid="trial-banner-dismiss">×</button>
        </div>
      )}      {consent && !consent.has_consent && (
        <ConsentGate status={consent} onDone={refreshConsent} />
      )}
      <ShortcutHint />
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-border/60 flex flex-col">
        <div className="h-16 px-4 flex items-center gap-2 font-display font-semibold border-b border-border/60">
          <Brain className="w-5 h-5" />
          Ragnes
        </div>
        <div className="px-3 py-3 border-b border-border/60">
          <WorkspaceSwitcher />
          {role && (
            <p className="mt-2 px-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground" data-testid="sidebar-current-role">
              Your role: <span className="text-foreground/80">{role}</span>
            </p>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-auto">
          {visibleNav.map((n) => {
            const badge = n.badgeKey ? badges[n.badgeKey] : 0;
            const isOnboardingRow = n.onboardingBadge && onboarding && !onboarding.all_complete && !onboarding.skipped;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors ${
                    isActive
                      ? "bg-secondary text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  }`
                }
              >
                <n.icon className="w-4 h-4" />
                <span className="flex-1">{n.label}</span>
                {isOnboardingRow && (
                  <span className="text-[10px] font-mono min-w-[26px] px-1.5 rounded-full bg-sky-500/15 text-sky-500 border border-sky-500/30" data-testid="nav-onboarding-progress">
                    {onboarding.completed_count}/{onboarding.total_count}
                  </span>
                )}
                {badge > 0 && (
                  <span className="text-[10px] font-mono min-w-[18px] h-[18px] px-1.5 rounded-full bg-emerald-500/15 text-emerald-500 border border-emerald-500/30 flex items-center justify-center" data-testid={`nav-badge-${n.tid}`}>
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border/60 space-y-2">
          {consent && consent.has_consent && !consent.capture_active && (
            <Link
              to="/app/settings"
              className="w-full flex items-center gap-2 px-3 py-2 rounded text-[11px] text-amber-600 dark:text-amber-400 border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
              data-testid="capture-off-banner"
            >
              <ShieldOff className="w-3.5 h-3.5 shrink-0" />
              Capture is off — your bot won't grow. Set up capture
            </Link>
          )}
          <button
            data-testid="theme-toggle-btn"
            onClick={toggle}
            className="w-full flex items-center justify-between px-3 py-2 rounded text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          >
            {theme === "dark" ? <><Sun className="w-4 h-4" /> Light</> : <><Moon className="w-4 h-4" /> Dark</>}
          </button>
          <div className="flex items-center gap-2 px-3 py-2 rounded bg-secondary/40 border border-border/40">
            <div className="w-7 h-7 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); nav("/"); }}
              className="text-muted-foreground hover:text-destructive transition-colors"
              aria-label="Log out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
      {/* Main */}
      <main className="flex-1 overflow-auto">
        {health?.rate_limit_warning && (
          <div
            data-testid="rate-limit-warning-banner"
            className="border-b border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-6 py-3 flex items-start gap-3"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <p className="font-medium">Rate-limit surge detected</p>
              <p className="text-xs opacity-90 mt-0.5">
                Over {Math.round((health.rate_limited_ratio || 0) * 100)}% of chat requests in the last 24h were throttled
                ({health.rate_limited}/{health.total}). Consider raising the workspace burst capacity or moving to a higher tier.
              </p>
            </div>
            <Link
              to="/app/gaps"
              className="text-xs underline underline-offset-4 hover:text-amber-500 self-center shrink-0"
              data-testid="rate-limit-warning-link"
            >
              View gaps
            </Link>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
